import React from "react";
import { Trash2 } from "lucide-react";

/**
 * Tabela genérica dos CRUDs de admin (orçamentos, contatos, newsletter).
 * `columns`: [{ key, label, render?(row) }]. `onDelete`/`statusOptions`
 * são opcionais — newsletter não tem status, por exemplo.
 */
export default function AdminTable({
  columns,
  rows,
  loading,
  error,
  emptyLabel = "Nada por aqui ainda.",
  statusOptions,
  onStatusChange,
  onDelete,
  getRowTestId,
}) {
  if (loading) {
    return <div className="py-16 text-center text-sm text-[var(--mn-dark)]">Carregando…</div>;
  }

  if (error) {
    return (
      <div className="py-16 text-center text-sm text-red-600">
        Não foi possível carregar os dados. Tente atualizar a página.
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="py-16 text-center text-sm text-[var(--mn-dark)] border border-dashed border-[var(--mn-line)] rounded-[4px]">
        {emptyLabel}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto border border-[var(--mn-line)] rounded-[4px] bg-white" data-testid="admin-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--mn-line)] bg-[var(--mn-cream)]">
            {columns.map((col) => (
              <th
                key={col.key}
                className="text-left px-4 py-3 text-[0.68rem] uppercase tracking-[0.12em] font-semibold text-[var(--mn-dark)] whitespace-nowrap"
              >
                {col.label}
              </th>
            ))}
            {statusOptions && (
              <th className="text-left px-4 py-3 text-[0.68rem] uppercase tracking-[0.12em] font-semibold text-[var(--mn-dark)] whitespace-nowrap">
                Status
              </th>
            )}
            {onDelete && <th className="w-10" />}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.id}
              className="border-b border-[var(--mn-line)] last:border-b-0 hover:bg-[var(--mn-cream)]/50"
              data-testid={getRowTestId ? getRowTestId(row) : undefined}
            >
              {columns.map((col) => (
                <td key={col.key} className="px-4 py-3 align-top text-[var(--mn-black)]">
                  {col.render ? col.render(row) : row[col.key]}
                </td>
              ))}
              {statusOptions && (
                <td className="px-4 py-3 align-top">
                  <select
                    value={row.status}
                    onChange={(e) => onStatusChange(row.id, e.target.value)}
                    className="text-xs border border-[var(--mn-line)] rounded-sm px-2 py-1.5 bg-white"
                    data-testid={`admin-status-${row.id}`}
                  >
                    {statusOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </td>
              )}
              {onDelete && (
                <td className="px-4 py-3 align-top text-right">
                  <button
                    type="button"
                    onClick={() => onDelete(row)}
                    className="p-1.5 text-[var(--mn-dark)] hover:text-red-600 transition-colors"
                    aria-label="Remover"
                    data-testid={`admin-delete-${row.id}`}
                  >
                    <Trash2 size={15} />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
